import React, { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { store } from "../../App";
import { setAddressOptions, makeSilentCallOnOpen } from "../../Redux/TrackerActions";
import { setToast, setLoader, clearLoader } from "../../Redux/OverlayActions";
import { useTranslation } from "../../hooks/useTranslation";
import "./SilentAddressView.css";

function SilentAddressView() {
    const { t } = useTranslation();

    const [formData, setFormData] = useState({
        straat: window.localStorage.getItem('straat') || '',
        nr: window.localStorage.getItem('nr') || '',
        gemeente: window.localStorage.getItem('gemeente') || '',
        postcode: window.localStorage.getItem('postcode') || '',
        land: window.localStorage.getItem('land') || t('addressPage.defaultCountry', 'België'),
    });

    const [validation, setValidation] = useState({
        straat: "",
        nr: "",
        gemeente: "",
        postcode: "",
        land: "",
    });

    const [continuesValidation, setContinuesValidation] = useState(false);
    const [redirectToTracker, setRedirectToTracker] = useState(false);
    const [redirectToAmbiguous, setRedirectToAmbiguous] = useState(false);

    const isEmpty = (window.localStorage.getItem('placeid') || '') === '';

    const validateForm = (showToast) => {
        let isValid = true;
        const newValidation = {
            straat: formData.straat ? "is-valid" : "is-invalid",
            nr: formData.nr ? "is-valid" : "is-invalid",
            gemeente: formData.gemeente ? "is-valid" : "is-invalid",
            postcode: formData.postcode ? "is-valid" : "is-invalid",
            land: formData.land ? "is-valid" : "is-invalid",
        };

        if (!formData.straat || !formData.nr || !formData.gemeente || !formData.postcode || !formData.land) {
            isValid = false;
        }

        setValidation(newValidation);

        if (!isValid && showToast) {
            store.dispatch(setToast({
                message: t('addressPage.errors.fillAllFields', 'Vul alle velden correct in'),
                theme: "error",
            }));
        }

        return isValid;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (continuesValidation) {
            setValidation(prev => ({
                ...prev,
                [name]: value ? "is-valid" : "is-invalid"
            }));
        }
    };

    const setAdresStorage = (latitude, longitude, adres, straat, nr, gemeente, postcode, land, place_id) => {
        window.localStorage.setItem('latitude', latitude);
        window.localStorage.setItem('longitude', longitude);
        window.localStorage.setItem('adres', adres);
        window.localStorage.setItem('straat', straat);
        window.localStorage.setItem('nr', nr);
        window.localStorage.setItem('gemeente', gemeente);
        window.localStorage.setItem('postcode', postcode);
        window.localStorage.setItem('land', land);
        window.localStorage.setItem('placeid', place_id);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm(true)) {
            setContinuesValidation(true);
            return;
        }

        store.dispatch(setLoader({
            title: t('loaders.pleaseWait', 'Even geduld...'),
            message: t('addressPage.loader.processing', 'We verwerken je adres'),
        }));

        try {
            const url = `${process.env.REACT_APP_API_HREF}legacycopy.php?action=get_latlng&straat=${formData.straat}&nr=${formData.nr}&gemeente=${formData.gemeente}&postcode=${formData.postcode}&land=${formData.land}`;
            const response = await fetch(url);
            store.dispatch(clearLoader());

            const data = await response.json();

            if (data.status === 200) {
                const result = data.result;
                setAdresStorage(
                    result.latitude,
                    result.longitude,
                    result.adres,
                    formData.straat,
                    formData.nr,
                    formData.gemeente,
                    formData.postcode,
                    formData.land,
                    result.placeid
                );
                store.dispatch(makeSilentCallOnOpen());
                setRedirectToTracker(true);
            } else if (data.status === 409) {
                store.dispatch(setAddressOptions(data.results));
                setRedirectToAmbiguous(true);
            } else {
                store.dispatch(setToast({
                    message: t('addressPage.errors.somethingWrong', 'Er ging iets mis. Probeer het later opnieuw.'),
                    theme: "error",
                }));
            }
        } catch (error) {
            console.error(error);
            store.dispatch(clearLoader());
            store.dispatch(setToast({
                message: t('addressPage.errors.somethingWrong', 'Er ging iets mis. Probeer het later opnieuw.'),
                theme: "error",
            }));
        }
    };

    if (redirectToTracker) {
        return <Navigate to="/" />;
    }

    if (redirectToAmbiguous) {
        return <Navigate to="/silentaddress/ambiguous" />;
    }

    const productName = t('company.productName', 'ijskar');

    return (
        <div className="address-page">
            <div className="address-header">
                <div className="address-header-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                </div>
                <h1>{isEmpty
                    ? t('addressPage.silent.titleEmpty', 'Adres invullen')
                    : t('addressPage.silent.titleEdit', 'Adres wijzigen')}</h1>
                <p>{isEmpty
                    ? t('addressPage.silent.subtitleEmpty', `Vul je adres in om een melding te krijgen wanneer een ${productName} in de buurt is`)
                    : t('addressPage.silent.subtitleEdit', 'Pas je adres aan om meldingen te krijgen op een andere locatie')}</p>
            </div>

            <form className="address-form" onSubmit={handleSubmit}>
                <div className="form-section">
                    <div className="form-row">
                        <div className="form-field">
                            <label htmlFor="straat">{t('addressPage.fields.street', 'Straat')}</label>
                            <input
                                type="text"
                                id="straat"
                                name="straat"
                                value={formData.straat}
                                onChange={handleChange}
                                placeholder={t('addressPage.placeholders.street', 'Bv. Chocoladelaan')}
                                className={`form-input ${validation.straat}`}
                            />
                        </div>
                        <div className="form-field nr-field">
                            <label htmlFor="nr">{t('addressPage.fields.number', 'Nr')}</label>
                            <input
                                type="text"
                                id="nr"
                                name="nr"
                                value={formData.nr}
                                onChange={handleChange}
                                placeholder="69"
                                className={`form-input ${validation.nr}`}
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-field postcode-field">
                            <label htmlFor="postcode">{t('addressPage.fields.postalCode', 'Postcode')}</label>
                            <input
                                type="text"
                                id="postcode"
                                name="postcode"
                                value={formData.postcode}
                                onChange={handleChange}
                                placeholder="9100"
                                className={`form-input ${validation.postcode}`}
                            />
                        </div>
                        <div className="form-field">
                            <label htmlFor="gemeente">{t('addressPage.fields.city', 'Gemeente')}</label>
                            <input
                                type="text"
                                id="gemeente"
                                name="gemeente"
                                value={formData.gemeente}
                                onChange={handleChange}
                                placeholder={t('addressPage.placeholders.city', 'Sint-Niklaas')}
                                className={`form-input ${validation.gemeente}`}
                            />
                        </div>
                    </div>

                    <div className="form-field">
                        <label htmlFor="land">{t('addressPage.fields.country', 'Land')}</label>
                        <input
                            type="text"
                            id="land"
                            name="land"
                            value={formData.land}
                            onChange={handleChange}
                            placeholder={t('addressPage.defaultCountry', 'België')}
                            className={`form-input ${validation.land}`}
                        />
                    </div>
                </div>

                <div className="form-actions">
                    <button type="submit" className="btn-submit">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        {t('addressPage.saveButton', 'Opslaan')}
                    </button>
                    <Link to="/" className="btn-cancel">
                        {t('addressPage.cancelButton', 'Annuleren')}
                    </Link>
                </div>
            </form>
        </div>
    );
}

export default SilentAddressView;
